import { Suspense, memo } from "react";
import { Route, Switch, Redirect, RouteComponentProps } from "react-router-dom";

import { ViewState } from "../components";
import { useAccount } from "../context/account";
import { useAuth } from "../context/auth";
import { useFeatureToggle } from "../context/featureToggle";
import { Tier } from "../types";

import { RoutesProps } from ".";

const RouteWithSubRoutes = memo(
  ({
    isPrivate,
    checkPermission,
    feature,
    redirect,
    path,
    component: Component,
    routes: subRoutes,
  }: RoutesProps): JSX.Element => {
    const { isAuthenticated } = useAuth();
    const { tier, availableMins } = useAccount();
    const { isFeatureEnabled } = useFeatureToggle();

    const renderComponent = (props: RouteComponentProps): JSX.Element => {
      const { location } = props;
      const { pathname } = location;
      const state = location.state as { from?: string } | undefined;

      if (isPrivate && !isAuthenticated) {
        return (
          <Redirect
            to={{
              pathname: redirect || "/login",
              state: { from: location },
            }}
          />
        );
      }
      if (feature && !isFeatureEnabled(feature)) {
        return <Redirect to="/live/events" />;
      }
      if (checkPermission) {
        if (
          pathname === "/live/new-event" &&
          tier === Tier.OFFICIAL &&
          typeof availableMins === "number" &&
          availableMins <= 0
        ) {
          return (
            <Redirect
              to={{
                pathname: "/pricing",
                state: { from: state?.from },
              }}
            />
          );
        }
        if (tier !== Tier.ENTERPRISE && (pathname === "/live/new-vod2live" || pathname === "/live/media")) {
          return (
            <Redirect
              to={{
                pathname: state?.from || "/live/events",
                state: { from: state?.from },
              }}
            />
          );
        }
      }

      return (
        // @ts-expect-error
        <Component routes={subRoutes} {...props} {...(state?.from ? { from: state.from } : {})} />
      );
    };
    return <Route path={path} render={renderComponent} />;
  }
);

const Routes = ({ routes }: RoutesProps): JSX.Element => (
  <Suspense fallback={<ViewState state="loading" />}>
    <Switch>
      {routes?.map((route, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <RouteWithSubRoutes key={i} {...route} />
      ))}
    </Switch>
  </Suspense>
);

export default memo(Routes);
